import { Button, useGetResourceLabel, useRecordContext, useResourceContext, useTranslate } from 'react-admin';
import { Edit as EditIcon } from '@mui/icons-material';
import useDialogContext from 'components/DialogContainer/useDialogContext';

interface EditSubItemProps {
  label?: string;
  /** Live: `useResourceContext(props)` below prefers it over the surrounding ResourceContext. */
  resource?: string;
  // TVR-2158: a `childre?: React.ReactElement` used to sit here — a typo for `children` that had
  // never had a consumer, since this button renders a fixed <EditIcon /> and no children at all.
  // Deleted rather than corrected: renaming it to `children` would have turned a typo into dead
  // code instead of removing it.
}
const EditSubItem = (props: EditSubItemProps) => {
  const resource = useResourceContext(props);
  const record = useRecordContext(props);
  const t = useTranslate();
  const getResourceLabel = useGetResourceLabel();
  const { openEditDialog, editForm } = useDialogContext();

  if (!editForm || !record) return null;

  // TVR-2153: the resource name comes from `useGetResourceLabel()`, the same hook AddSubItem uses,
  // so the two buttons on one screen cannot disagree. Calling `useTranslate()` directly on
  // `resources.${resource}.name` used to put the raw key on screen ("Edit
  // resources.hardware_revisions.name") whenever a resource had no catalogue entry — eight of them
  // have none today. The hook hands polyglot a per-call `_` fallback of humanize(inflect(resource,
  // 1)), which polyglot prefers over the missing-key path, so an absent entry degrades to readable
  // text instead. It also honours a <Resource options.label>, which the direct call ignored.
  //
  // No try/catch: neither hook can throw on a missing key — the provider is built with
  // `allowMissing: true` plus an `onMissingKey` that returns the key (src/providers/i18n), and
  // react-admin falls back to the identity function when no i18nProvider is mounted. Either way a
  // string comes back.
  const resourceLabel = resource ? getResourceLabel(resource, 1) : '';
  const label =
    props.label ||
    t('custom.actions.edit', {
      name: resourceLabel.replace(/^\w/, (c) => c.toLowerCase()),
      Name: resourceLabel,
    });

  return (
    <Button label={label} onClick={() => openEditDialog(record)}>
      <EditIcon />
    </Button>
  );
};

export default EditSubItem;
